import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Calendar, ChevronRight } from "lucide-react";
import { API, condClass, fmtDate, slug, siteToday } from "@/lib/api";
import { NextRaceBanner } from "@/components/NextRaceBanner";
import { raceMs, clock12 } from "@/lib/raceFormat";

const STATES = ["ALL", "VIC", "NSW", "QLD", "SA", "WA", "TAS", "ACT", "NT"];
const HERO = "https://images.unsplash.com/photo-1759063916119-e9302f91419d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const firstJump = (m) => Math.min(...(m.races || []).map((r) => raceMs(r, m.date)).filter(Boolean));

const MeetingCard = ({ m, i }) => (
  <Link to={`/meeting/${m.id}`} data-testid={`meeting-card-${slug(m.track)}`} style={{ animationDelay: `${i * 40}ms` }}
    className="rise group surface p-5 hover:border-blue-500/50 hover:bg-[#16213A] transition-colors">
    <div className="flex items-start gap-3">
      <div className="min-w-0">
        <p className="eyebrow">{m.state}</p>
        <h3 className="font-display text-xl font-bold tracking-tight text-slate-50 truncate">{m.track}</h3>
        <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{fmtDate(m.date)}{m.weather ? ` · ${m.weather}` : ""}</p>
      </div>
      <ChevronRight className="ml-auto w-5 h-5 shrink-0 text-slate-500 group-hover:text-blue-400 transition-colors" />
    </div>
    <div className="mt-4 pt-3 border-t border-[#202E4C] flex items-center gap-2 flex-wrap">
      {m.track_condition && <span className={`text-xs px-2 py-0.5 rounded-md border font-mono ${condClass(m.track_condition)}`}>{m.track_condition}</span>}
      <span className="chip">{m.races?.length || 0} races</span>
      {Number.isFinite(firstJump(m)) && <span className="chip" data-testid={`meeting-first-jump-${slug(m.track)}`}>R1 {clock12(firstJump(m))}</span>}
      {m.source === "krok" && <span data-testid={`meeting-live-${slug(m.track)}`} className="text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-500/30 rounded px-1.5">Live data</span>}
      {m.is_sample && <span className="text-[10px] uppercase tracking-widest text-slate-500 border border-white/10 rounded px-1.5">Sample</span>}
    </div>
  </Link>
);

export default function Home() {
  const [meetings, setMeetings] = useState(null);
  const [state, setState] = useState("ALL");
  const [date, setDate] = useState("ALL");
  useEffect(() => { axios.get(`${API}/meetings`).then((r) => setMeetings(r.data.filter((m) => m.date >= siteToday()).sort((a, b) => a.date.localeCompare(b.date)))); }, []);
  const dates = useMemo(() => [...new Set((meetings || []).map((m) => m.date))].sort(), [meetings]);
  const shown = (meetings || []).filter((m) => (state === "ALL" || m.state === state) && (date === "ALL" || m.date === date));

  return (
    <div>
      <section className="relative grain overflow-hidden border-b border-[#202E4C]">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B101D] via-[#0B101D]/85 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 flex items-center gap-10">
          <div className="flex-1">
          <p className="eyebrow mb-4">The Weekend Edge · Australian Racing Tips</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black italic uppercase tracking-tight max-w-3xl leading-[1.02] text-slate-50" data-testid="hero-heading">
            Read the form.<br /><span className="text-[#1E90FF]">Find the edge.</span>
          </h1>
          <p className="mt-4 text-base text-slate-300 max-w-xl">Live race cards and form for the major Australian city meetings, with hand-approved tips from The Weekend Edge.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/tips" data-testid="hero-tips-cta" className="px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-sm font-semibold transition-colors">Today's tips</Link>
            <Link to="/record" data-testid="hero-record-cta" className="px-5 py-2.5 rounded-full border border-[#202E4C] hover:border-blue-500/50 text-sm font-semibold text-slate-200 transition-colors">Results & strike rate</Link>
          </div>
          </div>
          <img src="/logo-512.png" alt="The Weekend Edge logo" data-testid="hero-logo"
            className="hidden lg:block w-64 h-64 rounded-2xl border border-[#202E4C] shadow-[0_8px_24px_rgba(0,0,0,0.5)]" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <NextRaceBanner meetings={meetings} />
        <div className="flex items-baseline justify-between gap-3 mb-4">
          <h2 className="section-title" data-testid="meetings-heading">Race meetings</h2>
          {meetings && <p className="text-xs text-slate-400" data-testid="meetings-count">{shown.length} meeting{shown.length === 1 ? "" : "s"}</p>}
        </div>
        <div className="flex flex-col gap-3 mb-6">
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="meetings-date-picker">
            {["ALL", ...dates].map((d) => (
              <button key={d} data-testid={`date-filter-${d}`} onClick={() => setDate(d)}
                className={`pill-btn ${date === d ? "pill-on" : "pill-off"}`}>
                {d === "ALL" ? "All dates" : fmtDate(d)}
              </button>
            ))}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {STATES.map((s) => (
              <button key={s} data-testid={`track-filter-${s.toLowerCase()}`} onClick={() => setState(s)}
                className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-bold tracking-widest border transition-colors ${state === s ? "bg-slate-100 text-slate-950 border-slate-100" : "border-[#202E4C] text-slate-400 hover:text-white"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        {meetings === null ? (
          <p className="text-slate-400">Loading meetings…</p>
        ) : shown.length === 0 ? (
          <div data-testid="meetings-empty" className="rounded-xl border border-dashed border-[#202E4C] p-10 text-center text-slate-400">
            <MapPin className="w-6 h-6 mx-auto mb-2" />{meetings.length ? "No meetings for this filter yet." : "No meetings imported for today or the coming days yet. Past results are on the Results page."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="meetings-grid">
            {shown.map((m, i) => <MeetingCard key={m.id} m={m} i={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
